import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import { EventEmitter } from 'fbemitter';
import { Howl } from '@rosetta/howler';
import { registerStoppableMedia, stopAllMedia } from '../utils/media-stopping-utils';
import { Ticker } from './Ticker';

var loadedSounds = [];

var addLoadedSound = function addLoadedSound(loadedSound) {
  if (loadedSound && !loadedSounds.includes(loadedSound)) {
    loadedSounds.push(loadedSound);
  }
};

var removeLoadedSound = function removeLoadedSound(loadedSound) {
  if (loadedSound) {
    var soundNdx = loadedSounds.findIndex(function (snd) {
      return snd === loadedSound;
    });
    if (soundNdx !== -1) {
      loadedSounds.splice(soundNdx, 1);
    }
  }
};

var stopAllSoundPlay = function stopAllSoundPlay() {
  loadedSounds.forEach(function (sound) {
    if (sound.playing()) {
      sound.pause();
    }
  });
};

//***************************************************************************************
export var AudioPlayer = function () {
  function AudioPlayer() {
    var _this = this;

    _classCallCheck(this, AudioPlayer);

    this.loadSound = function (soundUri) {
      // 'format' needed, streaming source
      // 'html5' for cors tolerance, buffering
      var sound = new Howl({
        src: [soundUri],
        format: ['mp3'],
        html5: true,
        autoplay: false
      });
      // subscribe to howler events
      sound.once('load', function () {
        _this.handleOnLoad(sound);
      });
      sound.once('loaderror', _this.handleOnLoadError);
      sound.once('playerror', _this.handleOnPlayError);
      sound.on('end', _this.handleOnEnd);
      sound.on('play', _this.handleOnPlay);
      sound.on('pause', _this.handleOnPause);
      sound.on('seek', _this.handleOnSeeked);
      return sound;
    };

    this._getCurrentSound = function () {
      try {
        return _this.soundQueue[_this.currentIndex].howlSound;
      } catch (e) {
        return null;
      }
    };

    this.pause = function () {
      var currSound = _this._getCurrentSound();
      if (currSound) {
        currSound.pause();
      }
      _this.ticker.stopTicking();
    };

    this.clearSoundLoadingWatcher = function () {
      if (_this.soundLoadingWatcherId) {
        clearInterval(_this.soundLoadingWatcherId);
        _this.soundLoadingWatcherId = null;
      }
    };

    this.play = function () {
      if (AudioPlayer.disabled) {
        console.warn('Audio player has been disabled! This usually indicates CI/Test environment.\n         If this is not the case, call nearest developer and cry.');
        return;
      }
      var currSound = _this._getCurrentSound();
      if (currSound && !currSound.playing()) {
        stopAllMedia();
        currSound.play();
        _this.ticker.startTicking();
      }
    };

    this.seek = function (seekToPosition) {
      var precedingSoundsDuration = 0;
      for (var ndx = 0; ndx < _this.soundQueue.length; ndx++) {
        var thisSoundDuration = _this.soundQueue[ndx].soundDuration;
        if (precedingSoundsDuration + thisSoundDuration >= seekToPosition) {
          _this.currentIndex = ndx;
          var thisSoundPosition = seekToPosition - precedingSoundsDuration;
          _this.handleOnSeekStart();
          _this.soundQueue[ndx].howlSound.seek(thisSoundPosition);
          break;
        }
        precedingSoundsDuration += thisSoundDuration;
      }
    };

    this.setTimeAsFraction = function (percentagePosition) {
      var newTime = percentagePosition * _this.duration;
      _this.seek(newTime);
    };

    this.isLastAudio = function () {
      return _this.soundQueue.length - 1 === _this.currentIndex;
    };

    this.handleOnLoad = function (sound) {
      var soundObj = _this.soundQueue.find(function (qObj) {
        return qObj.howlSound === sound;
      });

      soundObj.soundDuration = sound._sounds[0]._node.duration;
      addLoadedSound(soundObj.howlSound);
      soundObj.isLoaded = true;
      _this.emitter.emit('onSoundLoaded');
    };

    this.handleOnPlay = function () {
      _this.emitter.emit('onSoundPlay');
    };

    this.handleOnPause = function () {
      _this.emitter.emit('onSoundPause');
    };

    this.handleOnSeekStart = function () {
      _this.emitter.emit('onSoundSeekStart');
    };

    this.handleOnSeeked = function () {
      _this.emitter.emit('onSoundSeeked');
    };

    this.handleOnEnd = function () {
      _this.ticker.stopTicking();
      if (_this.isLastAudio()) {
        _this.emitter.emit('onSoundEnd');
      } else {
        _this.currentIndex++;
        _this.play();
        _this.handleOnNext();
      }
    };

    this.handleOnNext = function () {
      _this.emitter.emit('onNextSound', _this.currentIndex);
    };

    this.handleOnLoadError = function (err) {
      _this.error = err;
      _this.emitter.emit('onSoundLoadError');
      console.error('audioplayer: load sound error', err);
    };

    this.handleOnPlayError = function (err) {
      _this.error = err;
      console.error('audioplayer: play sound error', err);
    };

    this.clearMedia = function () {
      // clean up before destruction or reuse - stop all event listening etc
      _this.clearSoundLoadingWatcher();
      _this.ticker.stopTicking();
      _this.soundQueue.forEach(function (soundObj) {
        removeLoadedSound(soundObj.howlSound);
        soundObj.howlSound.unload();
        soundObj.soundDuration = -1;
        soundObj.isLoaded = false;
      });
      _this.soundQueue = [];
      _this.currentIndex = -1;
    };

    this.soundQueue = [];
    this.currentIndex = -1;
    this.tracked = false;
    this.error = '';
    this.soundLoadingWatcherId = null;
    this.emitter = new EventEmitter();
    this.ticker = new Ticker();
  }

  // Used for disabling all audio in CI/Test environment


  AudioPlayer.stopAll = function stopAll() {
    stopAllSoundPlay();
  };

  // expects either a single URI or an array of URIs pointing to audio


  AudioPlayer.prototype.setAudio = function setAudio(audio) {
    var _this2 = this;

    if (this.soundQueue.length > 0) {
      this.clearMedia();
    }
    var audioUriArray = Array.isArray(audio) ? audio : [audio];
    audioUriArray.forEach(function (audioUri) {
      _this2.soundQueue.push({
        audioUri: audioUri,
        howlSound: _this2.loadSound(audioUri),
        soundDuration: -1
      });
    });
    this.currentIndex = 0;
  };

  AudioPlayer.prototype.onPlay = function onPlay(handler) {
    return this.emitter.addListener('onSoundPlay', handler);
  };

  AudioPlayer.prototype.onPause = function onPause(handler) {
    return this.emitter.addListener('onSoundPause', handler);
  };

  AudioPlayer.prototype.onEnd = function onEnd(handler) {
    return this.emitter.addListener('onSoundEnd', handler);
  };

  AudioPlayer.prototype.onSeekStart = function onSeekStart(handler) {
    return this.emitter.addListener('onSoundSeekStart', handler);
  };

  AudioPlayer.prototype.onSeeked = function onSeeked(handler) {
    return this.emitter.addListener('onSoundSeeked', handler);
  };

  AudioPlayer.prototype.onLoaded = function onLoaded(handler) {
    return this.emitter.addListener('onSoundLoaded', handler);
  };

  AudioPlayer.prototype.onLoadError = function onLoadError(handler) {
    return this.emitter.addListener('onSoundLoadError', handler);
  };

  AudioPlayer.prototype.onNext = function onNext(handler) {
    return this.emitter.addListener('onNextSound', handler);
  };

  AudioPlayer.prototype.onTick = function onTick(handler) {
    return this.ticker.onTick(handler);
  };

  AudioPlayer.prototype.track = function track() {
    this.tracked = true;
  };

  // ****  Internal Event Handlers

  // Sound has loaded successfully


  // Sound has received a play command. Registration available.


  // Sound has received a pause command. Registration available.


  // Sound has received a seek command

  // Sound has gotten to where it seeked


  // Sound instance has completed. Registration available.
  // NOTE:  will emit onSoundEnd event if last sound in queue
  //   else will emit onNextSound event


  // Sound has errored on load. No callback registry.


  // Sound has errored on play. No callback registry.


  _createClass(AudioPlayer, [{
    key: 'isPlaying',


    // getters
    get: function get() {
      var currSound = this._getCurrentSound();
      return !!currSound && currSound.playing();
    }
  }, {
    key: 'isLoaded',
    get: function get() {
      if (this.currentIndex >= 0) {
        return this.soundQueue[this.currentIndex].isLoaded;
      }
      return false;
    }
  }, {
    key: 'isTracked',
    get: function get() {
      return this.tracked === true;
    }
  }, {
    key: 'duration',
    get: function get() {
      if (AudioPlayer.disabled) {
        return 0;
      }
      var allDuration = 0;
      this.soundQueue.forEach(function (audioObj) {
        allDuration += audioObj.soundDuration;
      });
      return allDuration;
    }
  }, {
    key: 'position',
    get: function get() {
      if (AudioPlayer.disabled) {
        return 0;
      }
      var soundPosition = 0;
      for (var ndx = 0; ndx < this.soundQueue.length; ndx++) {
        if (ndx === this.currentIndex) {
          var currentPos = this.soundQueue[ndx].howlSound.seek();
          if (typeof currentPos === 'number') {
            soundPosition += currentPos;
          }
          break;
        }
        soundPosition += this.soundQueue[ndx].soundDuration;
      }
      return soundPosition;
    }
  }, {
    key: 'progress',
    get: function get() {
      var soundDuration = this.duration;
      if (soundDuration === 0) {
        return 0;
      }
      return 1 - (soundDuration - this.position) / soundDuration;
    }
  }]);

  return AudioPlayer;
}();

AudioPlayer.disabled = false;
registerStoppableMedia(AudioPlayer);